import { Filter } from 'lucide-react';
import { memo, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@components/common/Dialog';
import { Button } from '@components/common/Button';
import { Input } from '@components/common/Input';
import { RxArrowTopRight } from 'react-icons/rx';
import CandidateKanbanBoard from './CandidateKanbanBoard';
import useCandidatePipelineStore from '@stores/candidatePipelineStore';

const CandidatePipeline = memo(() => {
    const {
        loadingCandidatePipeline,
        candidatePipelineError,
        searchQuery,
        isDialogOpen,
        candidatePipeline,
        fetchCandidatePipeline,
        setSearchQuery,
        setDialogOpen,
        clearStore,
    } = useCandidatePipelineStore();

    useEffect(() => {
        fetchCandidatePipeline();
        return clearStore;
    }, [fetchCandidatePipeline, clearStore]);

    const handleSearch = useCallback(
        event => {
            setSearchQuery(event.target.value);
        },
        [setSearchQuery],
    );

    const handleDialogChange = useCallback(() => {
        setDialogOpen(!isDialogOpen);
    }, [isDialogOpen, setDialogOpen]);

    return (
        <Dialog className="z-[99999]" open={isDialogOpen} onOpenChange={handleDialogChange}>
            <div className=" col-span-2 flex flex-col bg-white rounded-xl text-gray-900 shadow-card mb-4">
                <header className="flex items-center justify-between p-4">
                    <div className="flex items-center space-x-2">
                        <div className="text-lg font-bold">Candidate Pipeline</div>
                        <div className="text-sm text-gray-500">
                            ({candidatePipeline.length} candidates)
                        </div>
                    </div>
                    <DialogTrigger>
                        <Button variant="ghost" className="text-primary-blue font-semibold">
                            Expand
                            <RxArrowTopRight className="w-4 h-4 ml-2" />
                        </Button>
                    </DialogTrigger>
                </header>

                <DialogContent fullscreen={true} className="bg-gray-100" scrollEnabled={false}>
                    <DialogHeader>
                        <DialogTitle>Candidate Pipeline</DialogTitle>
                    </DialogHeader>
                    <div className="flex items-center gap-2 p-4 bg-white rounded-xl mb-2">
                        <Input
                            placeholder="Search candidates"
                            value={searchQuery}
                            onChange={handleSearch}
                            className="flex-1"
                        />
                        <Button variant="outline" className="shrink-0">
                            <Filter className="w-4 h-4 mr-2" />
                            Filter
                        </Button>
                    </div>
                    <CandidateKanbanBoard filteredCandidates={candidatePipeline} />
                </DialogContent>

                <div className="flex-1">
                    <CandidateKanbanBoard
                        filteredCandidates={candidatePipeline}
                        loading={loadingCandidatePipeline}
                        error={candidatePipelineError}
                    />
                </div>
            </div>
        </Dialog>
    );
});

CandidatePipeline.displayName = 'CandidatePipeline';

export default CandidatePipeline;
